import { computed, inject, provide, ref } from 'vue';
import { useClustersQuery, useCluster_EditMutation, useCluster_CreateMutation, useCluster_DeleteMutation } from '@/graphql/codegen/generated/graphql';
import { useNotify } from '@/services/notify/notify';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useI18n } from 'vue-i18n';
import { IKBuildCacheClusters } from './buildCacheClusters.injectionKeys';

export const useBuildCacheClustersProvider = () => {
  const { toastMessage } = useNotify();
  const { t } = useI18n();
  const GQL_useClustersQuery = useClustersQuery(undefined, { fetchPolicy: 'network-only', pollInterval: 10000 });
  const { mutate: GQL_EditCluster } = useCluster_EditMutation({});
  const { mutate: GQL_CreateCluster } = useCluster_CreateMutation({});
  const { mutate: GQL_DeleteCluster } = useCluster_DeleteMutation({});
  const searchInputVal = ref('');
  const sort = ref<{
    name: string;
    descending: boolean;
  }>({
    name: 'Name',
    descending: false,
  });
  const clusters = computed(() =>
    GQL_useClustersQuery.result.value?.clusters
      ?.filter(cluster => {
        const searchQueryLower = searchInputVal?.value?.toLowerCase() || '';
        return cluster?.name?.toLowerCase().includes(searchQueryLower);
      })
      .sort((clusterA, clusterB): number => {
        switch (sort.value?.name) {
          case 'Name':
            return (sort.value?.descending ? clusterB?.name.localeCompare(clusterA?.name || '') : clusterA?.name.localeCompare(clusterB?.name || '')) || 0;
          case 'Nodes':
            return sort.value?.descending ? (clusterB?.nodes?.length || 0) - (clusterA?.nodes?.length || 0) : (clusterA?.nodes?.length || 0) - (clusterB?.nodes?.length || 0);
          default:
            return 0;
        }
      }),
  );

  const noClusters = computed(() => !GQL_useClustersQuery.result.value?.clusters?.length);
  const newClusterField = ref<any>(null);
  const addNewClusterRow = () => {
    newClusterField.value = { name: '', nodes: [] };
  };
  const clustersList = computed(() => {
    if (newClusterField.value) {
      return [newClusterField.value, ...(clusters.value || [])];
    }
    return clusters.value;
  });

  const clearNewClusterRow = () => {
    newClusterField.value = null;
  };
  const isClusterNameExists = (name: string) => !!clusters.value?.some(cluster => cluster?.name === name);
  const editCluster = async (clusterId: number, newClusterName: string) => {
    try {
      await GQL_EditCluster({ id: clusterId, name: newClusterName });
      await GQL_useClustersQuery.refetch({});
    } catch (error) {
      toastMessage(t('agentsTable.postActionDialogText.toast.error'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
    }
  };

  const addNewCluster = async (newClusterName: string) => {
    try {
      await GQL_CreateCluster({ name: newClusterName });
      await GQL_useClustersQuery.refetch({});
    } catch (error) {
      toastMessage(t('agentsTable.postActionDialogText.toast.error'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
    }
  };

  const deleteCluster = async (clusterId: number) => {
    try {
      await GQL_DeleteCluster({ id: clusterId });
      await GQL_useClustersQuery.refetch({});
    } catch (error) {
      toastMessage(t('agentsTable.postActionDialogText.toast.error'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
    }
  };

  const payload = {
    searchInputVal,
    sort,
    clustersList,
    addNewClusterRow,
    clearNewClusterRow,
    isClusterNameExists,
    noClusters,
    editCluster,
    addNewCluster,
    deleteCluster,
  };
  provide(IKBuildCacheClusters, payload);
  return payload;
};
export const useBuildCacheClusters = () => {
  const buildCacheClusters = inject(IKBuildCacheClusters);
  if (!buildCacheClusters) throw new Error('useBuildCacheClusters must be used within useBuildCacheClustersProvider');
  return buildCacheClusters;
};
